import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { PayStation } from 'src/features/payment/utils/types';
import { useClearBanknotesAvailability } from 'src/features/payment/api/useClearBanknotesAvailability';
import { LIST_PAY_STATIONS_CASH_LEVELS_RESOURCE } from 'src/features/payment/api/useListPayStationCashLevels';
import { paymentsMessages } from 'src/features/payment/utils/messages';
import { useClearCoinsAvailability } from 'src/features/payment/api/useClearCoinsAvailability';
import { useParking } from 'src/providers/ParkingProvider';

interface ClearCashLevelsPayStationFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPayStation: PayStation;
    cashType: 'banknote' | 'coin';
    children?: React.ReactNode;
}

export default function ClearCashLevelsPayStationFormModal<TData>({
    selectedPayStation,
    cashType,
    children,
}: ClearCashLevelsPayStationFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: clearBanknoteLevels } = useClearBanknotesAvailability({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATIONS_CASH_LEVELS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(paymentsMessages.clearBanknoteLevelsSuccess),
                description: formatMessage(paymentsMessages.clearBanknoteLevelsSuccessDescription, {
                    payStation: selectedPayStation.name,
                }),
            });
        },
    });
    const { mutate: clearCoinLevels } = useClearCoinsAvailability({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_PAY_STATIONS_CASH_LEVELS_RESOURCE] });
            setIsModalOpen(false);

            toast({
                title: formatMessage(paymentsMessages.clearCoinLevelsSuccess),
                description: formatMessage(paymentsMessages.clearCoinLevelsSuccessDescription, {
                    payStation: selectedPayStation.name,
                }),
            });
        },
    });

    const modalIcon: IconProp = cashType === 'banknote' ? ICON_MAP.CASH : ICON_MAP.COIN;
    const modalTitle =
        cashType === 'banknote' ? paymentsMessages.clearBanknoteLevels : paymentsMessages.clearCoinLevels;
    const modalSubTitle =
        cashType === 'banknote'
            ? paymentsMessages.clearBanknoteLevelsDescription
            : paymentsMessages.clearCoinLevelsDescription;
    const modalConfirmationMsg =
        cashType === 'banknote' ? paymentsMessages.confirmClearBanknoteLevels : paymentsMessages.confirmClearCoinLevels;

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: {
                    ...modalTitle,
                },
                subTitle: {
                    ...modalSubTitle,
                    values: {
                        payStation: selectedPayStation.name,
                    },
                },
                icon: modalIcon,
                confirmationMessage: {
                    ...modalConfirmationMsg,
                    values: {
                        payStation: selectedPayStation.name,
                    },
                },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: paymentsMessages.clearLevels,
                callbackFn: () => {
                    if (cashType === 'banknote') {
                        return clearBanknoteLevels({
                            payStationId: selectedPayStation.id,
                            parkingId,
                        });
                    }
                    if (cashType === 'coin') {
                        return clearCoinLevels({
                            payStationId: selectedPayStation.id,
                            parkingId,
                        });
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}
